import { useState, useEffect, useRef } from 'react';
import { Palette, Sun, Moon, Monitor, Check } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type { AppTheme, AppColors } from '../types/theme';
import { fetchAllThemes, setMyTheme, applyThemeToDOM, storeThemeSlug } from '../services/themeService';
import type { UserRole } from '../lib/auth';

interface ThemeSwitcherProps {
  /** Kept for the header wiring; theme choice is now per-user for every role. */
  userRole?: UserRole | null;
  /** This user's chosen theme slug (null = org default). Drives the checkmark. */
  userThemeSlug?: string | null;
  onThemeChange?: (theme: AppTheme) => void;
  /** Active theme colors, so the header icon stays visible on every theme
   *  (without this it inherited a dark text color and vanished on the dark theme). */
  colors?: AppColors;
}

function getThemeIcon(slug: string, size: number) {
  switch (slug) {
    case 'light':
      return <Sun size={size} />;
    case 'dark':
      return <Moon size={size} />;
    default:
      return <Monitor size={size} />;
  }
}

export function ThemeSwitcher({ userThemeSlug, onThemeChange, colors }: ThemeSwitcherProps) {
  const { t } = useTranslation('common');
  const [themes, setThemes] = useState<AppTheme[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [activeTheme, setActiveTheme] = useState<AppTheme | null>(null);
  const [switching, setSwitching] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    loadThemes();
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  useEffect(() => {
    // Refresh the theme list when the admin changes the org default / theme set.
    const handleThemeUpdate = () => loadThemes();
    window.addEventListener('themeUpdated', handleThemeUpdate);
    return () => window.removeEventListener('themeUpdated', handleThemeUpdate);
  }, []);

  // The theme currently applied for THIS user: their personal pick if set and it
  // still exists, otherwise the org default (is_active).
  useEffect(() => {
    if (themes.length === 0) return;
    const mine = userThemeSlug ? themes.find((th) => th.slug === userThemeSlug) : undefined;
    setActiveTheme(mine ?? themes.find((th) => th.is_active) ?? null);
  }, [themes, userThemeSlug]);

  const loadThemes = async () => {
    try {
      setThemes(await fetchAllThemes());
    } catch (err) {
      console.error('Failed to load themes:', err);
    }
  };

  const handleSelectTheme = async (theme: AppTheme) => {
    if (theme.id === activeTheme?.id || switching) return;
    setSwitching(true);
    try {
      await setMyTheme(theme.slug); // persist this user's personal choice
      applyThemeToDOM(theme.app_colors);
      storeThemeSlug(theme.slug);
      setActiveTheme(theme);
      onThemeChange?.(theme);
    } catch (err) {
      console.error('Failed to set theme:', err);
    } finally {
      setSwitching(false);
      setIsOpen(false);
    }
  };

  const builtinThemes = themes.filter((th) => th.is_builtin);
  const customThemes = themes.filter((th) => !th.is_builtin);

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-1.5 px-3 py-2 rounded-lg transition-colors text-sm"
        style={{
          backgroundColor: isOpen ? 'rgba(0,0,0,0.08)' : 'transparent',
          color: colors?.text_primary_color,
        }}
        onMouseEnter={(e) => {
          if (!isOpen) e.currentTarget.style.backgroundColor = 'rgba(0,0,0,0.05)';
        }}
        onMouseLeave={(e) => {
          if (!isOpen) e.currentTarget.style.backgroundColor = 'transparent';
        }}
        title={t('themes.switchTheme')}
      >
        <Palette size={16} />
      </button>

      {isOpen && (
        <div className="absolute right-0 mt-1 w-56 bg-surface rounded-xl shadow-xl border border-line py-2 z-50 animate-scale-in">
          <div className="px-3 py-1.5 text-xs font-semibold text-theme-secondary uppercase tracking-wider">
            {t('themes.title')}
          </div>

          {builtinThemes.map((theme) => (
            <button
              key={theme.id}
              onClick={() => handleSelectTheme(theme)}
              disabled={switching}
              className={`w-full flex items-center gap-3 px-3 py-2.5 text-sm transition-colors ${
                theme.id === activeTheme?.id
                  ? 'bg-blue-50 text-blue-700 dark:bg-blue-500/10 dark:text-blue-300'
                  : 'text-theme-primary hover:bg-subtle'
              }`}
            >
              <div
                className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 border"
                style={{
                  backgroundColor: theme.app_colors.header_bg_color,
                  borderColor: theme.app_colors.border_color,
                  color: theme.app_colors.header_text_color,
                }}
              >
                {getThemeIcon(theme.slug, 14)}
              </div>
              <div className="flex-1 text-left">
                <div className="font-medium">{theme.name}</div>
                <div className="text-xs text-theme-secondary">
                  {theme.slug === 'light' && t('themes.lightDesc')}
                  {theme.slug === 'dark' && t('themes.darkDesc')}
                  {theme.slug === 'greyscale' && t('themes.greyscaleDesc')}
                </div>
              </div>
              {theme.id === activeTheme?.id && (
                <Check size={16} className="text-blue-600 flex-shrink-0" />
              )}
            </button>
          ))}

          {customThemes.length > 0 && (
            <>
              <div className="border-t border-line my-1" />
              <div className="px-3 py-1.5 text-xs font-semibold text-theme-secondary uppercase tracking-wider">
                {t('themes.custom')}
              </div>
              {customThemes.map((theme) => (
                <button
                  key={theme.id}
                  onClick={() => handleSelectTheme(theme)}
                  disabled={switching}
                  className={`w-full flex items-center gap-3 px-3 py-2.5 text-sm transition-colors ${
                    theme.id === activeTheme?.id
                      ? 'bg-blue-50 text-blue-700 dark:bg-blue-500/10 dark:text-blue-300'
                      : 'text-theme-primary hover:bg-subtle'
                  }`}
                >
                  <div className="flex gap-0.5 flex-shrink-0">
                    <div
                      className="w-4 h-8 rounded-l-md"
                      style={{ backgroundColor: theme.app_colors.primary_color }}
                    />
                    <div
                      className="w-4 h-8 rounded-r-md"
                      style={{ backgroundColor: theme.app_colors.header_bg_color }}
                    />
                  </div>
                  <div className="flex-1 text-left">
                    <div className="font-medium">{theme.name}</div>
                  </div>
                  {theme.id === activeTheme?.id && (
                    <Check size={16} className="text-blue-600 flex-shrink-0" />
                  )}
                </button>
              ))}
            </>
          )}
        </div>
      )}
    </div>
  );
}
